import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTenantActions } from 'utils/hooks/useTenantConfig';
import {
  Button,
  FormControlLabel,
  Input,
  Radio,
  RadioGroup,
  Switch,
  Typography,
  useEffectOnce
} from '@beeline.com/bp-design-system';
import { EngagementStatusSettings } from './EngagementStatus.types';
import { mergeMultiEngagementSettings } from './mergeEngagementStatusSettings';
import { useEngagementStatusSelector } from './useEngagementStatusSelector';
import { useEngagementStatusIntl } from './translations/useEngagementStatusIntl';

export const EngagementStatus: FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useEngagementStatusIntl();
  const [settings, setSettings] = useState<EngagementStatusSettings>();
  const { updateFeature } = useTenantActions();
  const initialSettings = useEngagementStatusSelector();
  const [hasChanged, setHasChanged] = useState(false);

  useEffectOnce(() => {
    initialiseSettings();
  });

  const initialiseSettings = useCallback(() => {
    if (initialSettings.engagementOffboardAutoTransition) {
      setSettings({
        enabled: initialSettings.enabled,
        status: initialSettings.status,
        daysShift: initialSettings.daysShift,
        useWorkerTimezone: initialSettings.useWorkerTimezone
      });
    }
  }, [initialSettings]);

  const handleSubmit = useCallback(async () => {
    if (settings && initialSettings.engagementOffboardAutoTransition) {
      await updateFeature(
        'engagement-offboard-auto-transition',
        mergeMultiEngagementSettings(settings, initialSettings.engagementOffboardAutoTransition),
        true
      );
    }
  }, [settings, initialSettings.engagementOffboardAutoTransition, updateFeature]);

  const handleCancel = useCallback(() => {
    initialiseSettings();
    setHasChanged(false);
  }, [initialiseSettings]);

  const handleUpdateSettings = useCallback((updatedSettings: Partial<EngagementStatusSettings>) => {
    setSettings((previousSettings) => ({ ...(previousSettings as EngagementStatusSettings), ...updatedSettings }));
    setHasChanged(true);
  }, []);

  if (!settings) return <div data-test="engagement-status-empty-container" />;

  return (
    <div data-test="engagement-status-container">
      <StyledSection>
        <Typography variant="h4">{intl.changeStatusAutomatically}</Typography>
        <Typography variant="body1">{intl.enableAndConfigureAutomaticStatusChanges}</Typography>

        {/* Auto Offboarding */}
        <StyledSetting>
          <Switch
            data-test="auto-offboarding"
            checked={settings.enabled}
            onChange={(value) => handleUpdateSettings({ enabled: value.target.checked })}
          />
          <StyledOption>
            <Typography variant="label1">{intl.autoOffboarding}</Typography>
            <Typography variant="body1">{intl.engagementsWillBeAutoOffboarded}</Typography>
          </StyledOption>
        </StyledSetting>

        {/* Status */}
        <StyledColumnSetting data-test="status-column-setting" disabled={!settings.enabled}>
          <Typography variant="label1">{intl.status}</Typography>
          <Typography variant="body1">{intl.selectStatusAfterAutoOffboarding}</Typography>

          <StyledRadioGroup
            dataTest="status-radio-group"
            name="status-radio-group"
            onChange={(_e, value) => {
              handleUpdateSettings({ status: value as EngagementStatusSettings['status'] });
            }}
            value={settings.status}
          >
            <FormControlLabel
              control={<Radio dataTest="offboarding-radio" />}
              label={<Typography variant="label1">{intl.offboarding}</Typography>}
              value="OFFBOARDING"
            />
            <FormControlLabel
              control={<Radio dataTest="offboarded-radio" />}
              label={<Typography variant="label1">{intl.offboarded}</Typography>}
              value="OFFBOARDED"
            />
          </StyledRadioGroup>
        </StyledColumnSetting>

        {/* Date */}
        <StyledColumnSetting data-test="date-column-setting" disabled={!settings.enabled}>
          <Typography variant="label1">{intl.date}</Typography>
          <Typography variant="body1">{intl.selectDateForAutomaticTransition}</Typography>

          <StyledRadioGroup name="date-radio-group" dataTest="date-radio-group" value={settings.daysShift}>
            <FormControlLabel
              checked={settings.daysShift === 0}
              control={<Radio dataTest="on-engagement-end-date-radio" />}
              label={<Typography variant="label1">{intl.onEngagementEndDate}</Typography>}
              onChange={() => {
                handleUpdateSettings({ daysShift: 0 });
              }}
              value={0}
            />
            <FormControlLabel
              checked={settings.daysShift > 0}
              control={<Radio dataTest="on-an-offset-date-radio" />}
              label={<Typography variant="label1">{intl.onAnOffsetDate}</Typography>}
              onChange={() => {
                handleUpdateSettings({ daysShift: 1 });
              }}
              value={1}
            />
            <StyledOffsetStatusChange disabled={settings.daysShift === 0}>
              <Typography variant="body1">{intl.offsetTheStatusChangeBy}</Typography>
              <StyledInputDays
                dataTest="offset-days-input"
                slotProps={{ htmlInput: { type: 'number', min: MIN_OFFSET_DAYS, max: MAX_OFFSET_DAYS } }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.value !== '') {
                    const updatedDays = parseInt(e.target.value, 10);
                    handleUpdateSettings({ daysShift: Math.min(updatedDays, MAX_OFFSET_DAYS) });
                  } else {
                    handleUpdateSettings({ daysShift: MIN_OFFSET_DAYS });
                  }
                }}
                type="number"
                value={settings.daysShift}
              />
              <Typography variant="body1">{intl.days({ days: settings.daysShift })}</Typography>
            </StyledOffsetStatusChange>
          </StyledRadioGroup>
        </StyledColumnSetting>

        {/* Timezone */}
        <StyledColumnSetting data-test="timezone-column-setting" disabled={!settings.enabled}>
          <Typography variant="label1">{intl.timezone}</Typography>
          <Typography variant="body1">{intl.selectTimezoneForAutomaticTransition}</Typography>

          <StyledRadioGroup
            name="time-radio-group"
            dataTest="time-radio-group"
            onChange={(_e, value) => {
              handleUpdateSettings({ useWorkerTimezone: value === 'true' });
            }}
            value={settings.useWorkerTimezone}
          >
            <FormControlLabel
              control={<Radio dataTest="timezone-radio" />}
              label={<Typography variant="label1">{intl.engagementBusinessSiteTimezone}</Typography>}
              value="true"
            />
            <FormControlLabel
              control={<Radio dataTest="utc-radio" />}
              label={<Typography variant="label1">{intl.utc}</Typography>}
              value="false"
            />
          </StyledRadioGroup>
        </StyledColumnSetting>
      </StyledSection>
      <StyledSection>
        <StyledCancelButton dataTest="status-cancel" disabled={!hasChanged} onClick={handleCancel} variant="text">
          {intl.cancel}
        </StyledCancelButton>
        <Button
          dataTest="status-save"
          disabled={!hasChanged}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        >
          {intl.save}
        </Button>
      </StyledSection>
    </div>
  );
};

export const MIN_OFFSET_DAYS = 1;
export const MAX_OFFSET_DAYS = 365;

const StyledSection = styled.div`
  padding-top: ${({ theme }) => theme.spacingSize.xxl};
`;

const StyledSetting = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  margin-top: ${({ theme }) => theme.spacingSize.l};
`;

const StyledColumnSetting = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacingSize.l};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const StyledRadioGroup = styled(RadioGroup)`
  align-items: flex-start;
`;

const StyledOption = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: ${({ theme }) => theme.spacingSize.xxl};
`;

const StyledOffsetStatusChange = styled.div<{ disabled?: boolean }>`
  align-items: center;
  display: flex;
  margin-top: 0.5rem;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const StyledInputDays = styled(Input)`
  && {
    input {
      min-width: 50px; // Keep the spinner within the input.
    }
    margin: 0 0.5rem;
    width: 60px;
  }
`;

const StyledCancelButton = styled(Button)`
  && {
    margin-right: ${({ theme }) => theme.spacingSize.l};
  }
`;
